import Konva from 'konva';
import { Layer, Rect, Transformer } from 'react-konva';
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { updateElements, isOverseas } from '@shakkerai/pro-utils';
import { useViewport, useProjectState, getProjectState, setProjectState, getViewport } from '@starflow/app/store';
import { setTempState, useTempState } from '@starflow/app/store';
import { getConfigs } from '@starflow/app/extension/elements';
import { mapUpdateElements } from '@starflow/app/helpers/element';
import { clearSelection, updateSelection } from '@starflow/app/helpers/canvas';
import { getSelectionToolBarComponents } from '@starflow/app/helpers/component';
import { getSharedStage } from '.';

const defaultResizeAnchors = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];

const TransformerCore = React.memo(
  React.forwardRef(({ scale, selection, ...props }, ref) => {
    const dragDisabled = useTempState('dragDisabled');
    const resizeDisabled = useTempState('resizeDisabled');
    const strokeColor = isOverseas ? '#2EE8C7' : '#379AFF';

    // 如果当前只选中一个了一个类型的元素，那么根据获取元素配置判断是否可以拖拽、缩放、旋转等
    const { resizable, draggable, rotateEnabled, flipEnabled, keepRatio, resizeAnchors } = useMemo(() => {
      const defaultConfig = {
        resizable: !resizeDisabled,
        draggable: !dragDisabled,
        // todos fqy 这里先不放开了
        rotateEnabled: false,
        flipEnabled: false,
        keepRatio: true,
        resizeAnchors: defaultResizeAnchors,
      };

      if (selection.length !== 1) return defaultConfig;

      const elementType = selection[0].split('-')[1];
      const elementConfig = getConfigs()[elementType];

      return Object.assign(defaultConfig, elementConfig);
    }, [selection]);

    const handleClick = useCallback(() => {
      // ...
    }, []);

    const handleMouseOver = useCallback(() => {
      if (selection.length === 1) {
        clearTimeout(window.mouseLeaveTimer);
        setTempState({ hoveringElementId: selection[0] });
      }
    }, [selection]);

    const handleMouseOut = useCallback(() => {
      clearTimeout(window.mouseLeaveTimer);

      window.mouseLeaveTimer = setTimeout(() => {
        setTempState({ hoveringElementId: null });
      }, 100);
    }, []);

    useEffect(() => {
      if (draggable || resizable || rotateEnabled) {
        const stage = ref.current?.getStage();
        const elements = stage?.findOne('#elementsLayer').children;
        const selectionMap = Object.fromEntries(selection.map((item) => [item, true]));
        const selectedKonvaNodes = elements.filter((element) => selectionMap[element.name()]);

        ref.current?.nodes(selectedKonvaNodes || []);
      }
    }, [ref, selection, draggable, resizable, rotateEnabled]);

    if (!draggable && !resizable && !rotateEnabled) return null;

    return (
      <Transformer
        {...props}
        ref={ref}
        id="mainTransformer"
        draggable={draggable}
        rotateEnabled={rotateEnabled}
        useSingleNodeRotation={true}
        keepRatio={keepRatio}
        anchorSize={6}
        flipEnabled={flipEnabled}
        resizeEnabled={resizable}
        enabledAnchors={resizeAnchors}
        // shouldOverdrawWholeArea={true}
        anchorFill="#fff"
        borderStrokeWidth={1}
        borderStroke={strokeColor}
        anchorStroke={strokeColor}
        onClick={handleClick}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
      />
    );
  }),
);

export const Selection = React.memo(() => {
  const transformerRef = useRef(null);
  const selectionLayerRef = useRef(null);
  const refData = useRef({});
  const viewport = useViewport();
  const fillColor = isOverseas ? '#2EE8C710' : '#379AFF10';
  const strokeColor = isOverseas ? '#2EE8C7' : '#379AFF';

  const selection = useProjectState('selection');
  const currentCanvasTool = useTempState('currentCanvasTool');
  const currentElementTool = useProjectState('currentElementTool');
  const spacePressing = useTempState('spacePressing');
  const selectionRect = useTempState('selectionRect');

  // TODO 后续根据currentElementTool的配置来判断是否要禁用选区交互
  const selectionDisabled = currentElementTool || spacePressing || currentCanvasTool !== 'select';

  const handleMouseDown = useCallback(
    (event) => {
      if (event.evt.button === 2) return;
      if (getProjectState('editingElementId')) return;

      if (event.target instanceof Konva.Stage) {
        if (selectionDisabled) {
          setTempState({ dragging: true });
        } else {
          setTempState({ selecting: true });
          const { x, y } = event.target.getPointerPosition();
          refData.current.startPos = [x, y];
          setTempState({ selectionRect: [x, y, 0, 0] });
          event.evt.preventDefault();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectionDisabled],
  );

  const handleMouseMove = useCallback(
    (event) => {
      if (refData.current.startPos) {
        if (!selectionDisabled) {
          const currentPos = event.currentTarget.getPointerPosition();

          const nextX = Math.min(refData.current.startPos[0], currentPos.x);
          const nextY = Math.min(refData.current.startPos[1], currentPos.y);
          const nextWidth = Math.abs(currentPos.x - refData.current.startPos[0]);
          const nextHeight = Math.abs(currentPos.y - refData.current.startPos[1]);

          setTempState({
            contextMenuData: null,
            contextMenuTarget: null,
            contextMenuPosition: null,
            selectionRect: [nextX, nextY, nextWidth, nextHeight],
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectionDisabled],
  );

  const handleMouseUp = useCallback(
    (event) => {
      if (getProjectState('editingElementId')) {
        return;
      }

      if (refData.current.startPos) {
        const elements = event.currentTarget.findOne('#elementsLayer').children;
        const selectionRectNode = event.currentTarget.findOne('#selectionRect');

        if (selectionRectNode) {
          const selectionRectNodeRect = selectionRectNode.getClientRect();
          const nextSelection = elements
            ?.filter((element) => {
              return (
                !element.attrs.unselectable &&
                element.attrs.role === 'element' &&
                Konva.Util.haveIntersection(selectionRectNodeRect, element.getClientRect())
              );
            })
            .map((element) => element.name());
          updateSelection(nextSelection);
          setTempState({ dragging: false, selecting: false, selectionRect: null });
        } else {
          clearSelection();
          setTempState({ dragging: false, selecting: false, selectionRect: null });
        }
      } else {
        setTempState({ dragging: false, selecting: false, selectionRect: null });
      }

      refData.current.startPos = null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleTransformStart = useCallback(() => {
    setTempState({ transforming: true });
  }, []);

  const handleTransformEnd = useCallback(
    () => {
      const stage = selectionLayerRef.current?.getStage();
      const { elements } = getProjectState();
      const nodes = selection.map((id) => stage?.findOne(`.${id}`)).filter((i) => i);

      const updatedElements = nodes.reduce((acc, node) => {
        const nodeId = node.name();
        const { x, y, scaleX, scaleY, rotation } = node.attrs;

        if (nodeId.startsWith('element')) {
          acc[nodeId] = { x, y, scaleX, scaleY, rotation };

          if (nodeId.startsWith('element-ellipse')) {
            Object.assign(acc[0][nodeId], { x: x - scaleX / 2, y: y - scaleY / 2 });
          }
        }

        return acc;
      }, {});

      // TODO
      // 增加钩子进行拦截

      setTempState({ transformerUpdateKey: Date.now(), transforming: false });
      setProjectState({ elements: mapUpdateElements(elements, updatedElements) });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selection],
  );

  const handleDragStart = useCallback(() => {
    setTempState({ transforming: true });
  }, []);

  const handleDragEnd = useCallback(
    () => {
      const { elements } = getProjectState();
      const stage = selectionLayerRef.current?.getStage();
      const nodes = selection.map((id) => stage?.findOne(`.${id}`)).filter((i) => i);

      const updatedElements = nodes.reduce((acc, node) => {
        const nodeId = node.name();
        const { x, y } = node.attrs;

        if (nodeId.startsWith('element')) {
          acc[nodeId] = { x, y };
        }

        return acc;
      }, {});

      setTempState({ transformerUpdateKey: Date.now(), transforming: false });
      setProjectState({ elements: updateElements(elements, updatedElements) });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selection],
  );

  useEffect(() => {
    if (!selectionDisabled) {
      const stage = selectionLayerRef.current?.getStage();
      const elements = stage?.findOne('#elementsLayer').children;
      const selectionMap = Object.fromEntries(selection.map((item) => [item, true]));
      const selectedKonvaNodes = elements.reduce((res, element) => {
        if (selectionMap[element.name()]) {
          res.push(element);
        } else if (element.children) {
          element.children.forEach((item) => {
            if (selectionMap[item.name()]) res.push(item);
          });
        }

        return res;
      }, []);

      transformerRef.current?.nodes(selectedKonvaNodes || []);
    }
  }, [selectionDisabled, selection]);

  useEffect(() => {
    const stage = selectionLayerRef.current?.getStage();
    stage.on('mousedown.selection', handleMouseDown);

    return () => {
      stage.off('mousedown.selection');
    };
  }, [handleMouseDown]);

  useEffect(() => {
    const stage = selectionLayerRef.current?.getStage();
    stage.on('mousemove.selection', handleMouseMove);

    return () => {
      stage.off('mousemove.selection');
    };
  }, [handleMouseMove]);

  useEffect(() => {
    const stage = selectionLayerRef.current?.getStage();
    stage.on('mouseup.selection', handleMouseUp);

    return () => {
      stage.off('mouseup.selection');
    };
  }, [handleMouseUp]);

  useEffect(() => {
    return () => {
      refData.current = {};
    };
  }, []);

  return (
    <Layer ref={selectionLayerRef}>
      {!selectionDisabled && (
        <TransformerCore
          selection={selection}
          ref={transformerRef}
          scale={viewport.scale}
          onDragStart={handleDragStart}
          onTransformStart={handleTransformStart}
          onDragEnd={handleDragEnd}
          onTransformEnd={handleTransformEnd}
        />
      )}
      {selectionRect && (
        <Rect
          id="selectionRect"
          x={(selectionRect[0] - viewport.x) / viewport.scale}
          y={(selectionRect[1] - viewport.y) / viewport.scale}
          scale={{ x: 1 / viewport.scale, y: 1 / viewport.scale }}
          width={selectionRect[2]}
          height={selectionRect[3]}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={1}
          strokeScaleEnabled={false}
        />
      )}
    </Layer>
  );
});

const SelectionOverLayerInner = React.memo(({ selection }) => {
  const renderedComponents = useMemo(() => {
    return getSelectionToolBarComponents().map(({ Component }, index) => (
      <Component key={index} selection={selection} />
    ));
  }, [selection]);

  return renderedComponents;
});

export const SelectionOverLayer = React.memo(() => {
  const selection = useProjectState('selection');

  const transforming = useTempState('transforming');
  const transformerUpdateKey = useTempState('transformerUpdateKey');
  const viewport = useViewport();
  const [rect, setRect] = useState(null);

  useEffect(() => {
    (async () => {
      const elements = getProjectState('elements');
      if (
        (selection.length > 1 || (selection.length === 1 && selection[0].startsWith('element-group'))) &&
        !transforming
      ) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
        const sharedStage = getSharedStage();
        const transformerRef = sharedStage.findOne('#mainTransformer');

        if (transformerRef) {
          const currentScale = getViewport().scale;
          const rect = transformerRef.getClientRect();

          let newRect;
          if (selection.length === 1 && selection[0].startsWith('element-group')) {
            const aimElement = elements.find((item) => item.id === selection[0]);
            const anchorSize = transformerRef.anchorSize();
            newRect = {
              x: aimElement.x - anchorSize / currentScale / 2,
              y: aimElement.y - anchorSize / currentScale / 2,
              width: aimElement.width * aimElement.scaleX + anchorSize / currentScale,
              height: aimElement.height * aimElement.scaleY + anchorSize / currentScale,
            };
          } else {
            newRect = {
              x: (rect.x - viewport.x) / currentScale,
              y: (rect.y - viewport.y) / currentScale,
              width: rect.width / currentScale,
              height: rect.height / currentScale,
            };
          }
          setRect(newRect);
        }
      } else {
        setRect(null);
      }
    })();
  }, [transforming, selection, transformerUpdateKey, viewport.scale]);

  if (!rect || transforming) return null;

  const left = rect.x * viewport.scale + viewport.x;
  const top = rect.y * viewport.scale + viewport.y;
  const width = rect.width * viewport.scale;
  const height = rect.height * viewport.scale;
  return (
    <div style={{ top, left, width, height }} className="absolute pointer-events-none">
      <SelectionOverLayerInner selection={selection} />
    </div>
  );
});
